import { LOCALE } from '@/shared/lib/dates';

/** Formatação de texto e número para a interface, sempre em português. */

/** `1 projeto` · `3 projetos` */
export function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** `hoje` · `há 1 dia` · `há 12 dias` — usado na fila de análise (US-18). */
export function formatWaitingDays(days: number): string {
  if (days <= 0) return 'hoje';
  return `há ${pluralize(days, 'dia', 'dias')}`;
}

/** `Mateus Martins` a partir do nome completo — para listas densas. */
export function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return fullName.trim();
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

/** `MM` — avatar sem imagem. */
export function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * `(61) 99999-8888` a partir de `+5561999998888`.
 *
 * O banco guarda E.164 (`profiles.phone`, com CHECK) porque é o único formato
 * que sobrevive a discagem, exportação e comparação; a máscara de leitura mora
 * aqui, na borda, como o comentário daquela coluna manda.
 *
 * Número de fora do Brasil, ou fora do formato esperado, sai **como está** — e
 * não meio-mascarado. Um telefone parcialmente formatado é pior que um
 * cru: ele parece certo e disca errado.
 */
export function formatPhone(e164: string): string {
  const match = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164.trim());
  if (!match) return e164;
  return `(${match[1]}) ${match[2]}-${match[3]}`;
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

/** `Louvor, Missões e Jovens` */
export function formatList(items: readonly string[]): string {
  return new Intl.ListFormat(LOCALE, { style: 'long', type: 'conjunction' }).format(
    items as string[],
  );
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(LOCALE).format(value);
}

/**
 * `R$ 8.900,00` a partir de 890000.
 *
 * O banco guarda centavos em `bigint` (spec §4.2): dinheiro em float é erro de
 * arredondamento garantido. A divisão por 100 acontece aqui, na borda de
 * exibição, e em nenhum outro lugar.
 */
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
}

/**
 * `'R$ 1.200,50'` · `'1200,50'` · `'1.200'` → 120050 centavos.
 *
 * O voluntário digita como fala, e o campo aceita as três formas. A conversão
 * acontece só aqui, na borda de entrada, e o resto do sistema só conhece
 * centavos inteiros — o mesmo motivo de `formatCurrency` existir do outro lado.
 *
 * Devolve `null` para entrada vazia ou sem nenhum dígito: campo em branco é
 * "não informei", que é diferente de zero.
 */
export function parseCurrency(input: string): number | null {
  const digitsAndSeparators = input.replace(/[^\d,.]/g, '');
  if (!/\d/.test(digitsAndSeparators)) return null;

  // Separador decimal é o ÚLTIMO `,` ou `.` seguido de no máximo 2 dígitos —
  // é o que distingue "1.200" (mil e duzentos) de "1.20" (um e vinte).
  const decimalMatch = /[.,](\d{1,2})$/.exec(digitsAndSeparators);
  const decimals = decimalMatch?.[1] ?? '';
  const integerPart = decimalMatch
    ? digitsAndSeparators.slice(0, decimalMatch.index)
    : digitsAndSeparators;

  const integerDigits = integerPart.replace(/\D/g, '') || '0';
  const cents = `${integerDigits}${decimals.padEnd(2, '0')}`;

  const parsed = Number.parseInt(cents, 10);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
